<template>
  <v-container fluid>
    <v-row v-if="loading">
      <v-col cols="12" md="3" v-for="i in 4" :key="`sk-kpi-${i}`">
        <v-skeleton-loader type="card"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="7">
        <v-skeleton-loader type="table"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="5">
        <v-skeleton-loader type="list-item-three-line"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="6">
        <v-skeleton-loader type="table"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="6">
        <v-skeleton-loader type="table"></v-skeleton-loader>
      </v-col>
    </v-row>

    <template v-else>
    <!-- KPIs -->
    <v-row>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card">
          <v-card-text class="d-flex justify-space-between align-center">
            <div>
              <div>Ventas hoy</div>
              <div class="text-h5 font-weight-bold">${{ money(summary.kpis.salesToday) }}</div>
              <div class="text-caption grey--text">Últimos 30 días: ${{ money(summary.kpis.salesLast30Days) }}</div>
            </div>
            <v-icon color="blue" large>mdi-currency-usd</v-icon>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card">
          <v-card-text class="d-flex justify-space-between align-center">
            <div>
              <div>Clientes morosos</div>
              <div class="text-h5 font-weight-bold">{{ summary.kpis.delinquentCustomers }}</div>
              <div class="text-caption grey--text">Clientes con deuda activa</div>
            </div>
            <v-icon color="purple" large>mdi-account-multiple-plus-outline</v-icon>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card">
          <v-card-text class="d-flex justify-space-between align-center">
            <div>
              <div>Tickets hoy</div>
              <div class="text-h5 font-weight-bold">{{ summary.kpis.ticketsToday }}</div>
              <div class="text-caption grey--text">Total de tickets emitidos hoy</div>
            </div>
            <v-icon color="teal" large>mdi-package-variant</v-icon>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card">
          <v-card-text class="d-flex justify-space-between align-center">
            <div>
              <div>Productos en bajo stock</div>
              <div class="text-h5 font-weight-bold">{{ summary.kpis.lowStockProducts }}</div>
              <div class="text-caption grey--text">Con existencias &lt;= 5 unidades</div>
            </div>
            <v-icon color="indigo" large>mdi-alert-circle-outline</v-icon>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Ventas recientes + inventario -->
    <v-row>
      <v-col cols="12" md="7">
        <v-card class="primary-block">
          <v-card-title>Ventas recientes</v-card-title>
          <v-data-table
            :headers="recentSalesHeaders"
            :items="summary.recentSales"
            dense
            fixed-header
            height="340"
            disable-pagination
            hide-default-footer
            no-data-text="Sin ventas recientes"
          >
            <template #[`item.productsCount`]="{ item }">
              {{ Number(item.productsCount || 0).toFixed(2) }}
            </template>
            <template #[`item.dateNew`]="{ item }">
              {{ date(item.dateNew) }}
            </template>
            <template #[`item.total`]="{ item }">
              ${{ money(item.total) }}
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn small text color="primary" @click="openTicketDetail(item)">
                Ver detalle
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="primary-block">
          <v-card-title>Estado del inventario</v-card-title>
          <v-list dense class="inventory-scroll">
            <div v-for="(item, index) in summary.inventoryStatus" :key="index" class="pa-3">
              <div class="d-flex justify-space-between mb-1">
                <span class="font-weight-medium">{{ item.productName }}</span>
                <v-chip :color="stockMeta(item.totalUnits).color" small label text-color="white">
                  {{ stockMeta(item.totalUnits).text }}
                </v-chip>
              </div>
              <v-progress-linear
                :value="stockMeta(item.totalUnits).progress"
                :color="stockMeta(item.totalUnits).color"
                height="8"
                rounded
              ></v-progress-linear>
              <div class="d-flex justify-space-between text-caption grey--text mt-1">
                <span>{{ item.totalUnits }} unidades</span>
              </div>
            </div>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-1">
      <v-col cols="12" md="6">
        <v-card class="secondary-block">
          <v-card-title>Top productos (30 días)</v-card-title>
          <v-data-table
            :headers="topProductsHeaders"
            :items="summary.topProducts"
            dense
            fixed-header
            height="280"
            disable-pagination
            hide-default-footer
            no-data-text="Sin información"
          >
            <template #[`item.unitsSold`]="{ item }">
              {{ Number(item.unitsSold).toFixed(2) }}
            </template>
            <template #[`item.revenue`]="{ item }">
              ${{ money(item.revenue) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="secondary-block">
          <v-card-title>Clientes morosos</v-card-title>
          <v-data-table
            :headers="delinquentHeaders"
            :items="summary.delinquentCustomers"
            dense
            fixed-header
            height="280"
            disable-pagination
            hide-default-footer
            no-data-text="Sin clientes con deuda activa"
          >
            <template #[`item.currentDebt`]="{ item }">
              ${{ money(item.currentDebt) }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="detailDialog" max-width="900px">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Detalle de ticket {{ selectedTicketDetail.ticket?.ticketNumber || '-' }}</span>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense v-if="selectedTicketDetail.ticket">
            <v-col cols="12" md="4"><strong>Cliente:</strong> {{ selectedTicketDetail.ticket.customerName }}</v-col>
            <v-col cols="12" md="4"><strong>Productos:</strong> {{ Number(selectedTicketDetail.ticket.productsCount || 0).toFixed(2) }}</v-col>
            <v-col cols="12" md="4"><strong>Total:</strong> ${{ Number(selectedTicketDetail.ticket.total || 0).toFixed(2) }}</v-col>
          </v-row>
          <v-data-table
            :headers="detailHeaders"
            :items="selectedTicketDetail.lines"
            :loading="loadingDetail"
            dense
            disable-pagination
            hide-default-footer
            no-data-text="Sin líneas de ticket"
            class="mt-3"
          >
            <template #[`item.units`]="{ item }">
              {{ Number(item.units || 0).toFixed(2) }}
            </template>
            <template #[`item.price`]="{ item }">
              ${{ Number(item.price || 0).toFixed(2) }}
            </template>
            <template #[`item.subtotal`]="{ item }">
              ${{ Number(item.subtotal || 0).toFixed(2) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    </template>
  </v-container>
</template>

<script>
import * as dashboardService from '@/services/dashboardService';
import * as salesService from '@/services/salesService';
import { formatDateTimeByTimezone } from '@/utils/dateTime';

export default {
  name: 'AdminDashboardView',
  data() {
    return {
      timezoneVersion: 0,
      loading: false,
      summary: {
        kpis: {
          salesToday: 0,
          salesLast30Days: 0,
          ticketsToday: 0,
          delinquentCustomers: 0,
          lowStockProducts: 0,
        },
        recentSales: [],
        topProducts: [],
        inventoryStatus: [],
        delinquentCustomers: [],
      },
      recentSalesHeaders: [
        { text: 'Folio', value: 'ticketNumber', sortable: false },
        { text: 'Cliente', value: 'customerName', sortable: false },
        { text: 'Productos', value: 'productsCount', sortable: false, align: 'end' },
        { text: 'Fecha', value: 'dateNew', sortable: false },
        { text: 'Total', value: 'total', sortable: false, align: 'end' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      topProductsHeaders: [
        { text: 'Producto', value: 'productName', sortable: false },
        { text: 'Unidades', value: 'unitsSold', sortable: false, align: 'end' },
        { text: 'Ingreso', value: 'revenue', sortable: false, align: 'end' },
      ],
      delinquentHeaders: [
        { text: 'Código', value: 'searchKey', sortable: false },
        { text: 'Cliente', value: 'name', sortable: false },
        { text: 'Ciudad', value: 'city', sortable: false },
        { text: 'Deuda actual', value: 'currentDebt', sortable: false, align: 'end' },
      ],
      detailDialog: false,
      loadingDetail: false,
      selectedTicketDetail: {
        ticket: null,
        lines: [],
      },
      detailHeaders: [
        { text: '#', value: 'line', sortable: false, width: '60px' },
        { text: 'Producto', value: 'productName', sortable: false },
        { text: 'Cantidad', value: 'units', sortable: false, align: 'end' },
        { text: 'Precio', value: 'price', sortable: false, align: 'end' },
        { text: 'Subtotal', value: 'subtotal', sortable: false, align: 'end' },
      ],
    };
  },
  async mounted() {
    window.addEventListener('app-timezone-changed', this.handleTimezoneChanged);
    await this.loadSummary();
  },
  beforeDestroy() {
    window.removeEventListener('app-timezone-changed', this.handleTimezoneChanged);
  },
  methods: {
    handleTimezoneChanged() {
      this.timezoneVersion += 1;
      this.$forceUpdate();
    },
    async loadSummary() {
      this.loading = true;
      try {
        this.summary = await dashboardService.fetchDashboardSummary();
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar el dashboard.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    date(value) {
      return formatDateTimeByTimezone(value, '-');
    },
    stockMeta(units) {
      const n = Number(units || 0);
      if (n <= 0) return { text: 'Agotado', color: 'red', progress: 0 };
      if (n <= 5) return { text: 'Bajo', color: 'orange', progress: Math.min((n / 20) * 100, 100) };
      return { text: 'En stock', color: 'green', progress: Math.min((n / 50) * 100, 100) };
    },
    async openTicketDetail(ticket) {
      this.detailDialog = true;
      this.loadingDetail = true;
      this.selectedTicketDetail = { ticket: null, lines: [] };
      try {
        this.selectedTicketDetail = await salesService.fetchSalesTicketDetail(ticket.receiptId);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar detalle del ticket.', type: 'error' });
      } finally {
        this.loadingDetail = false;
      }
    },
  },
};
</script>

<style scoped>
/* Puedes añadir estilos específicos aquí si es necesario */
.text-caption {
  font-size: 0.75rem !important;
}

.kpi-card {
  min-height: 132px;
}

.primary-block {
  min-height: 420px;
}

.secondary-block {
  min-height: 360px;
}

.inventory-scroll {
  max-height: 340px;
  overflow-y: auto;
}
</style> 